window.ttx = window.ttx || {};
ttx.ajax = {};

/** API 网关前缀，所有以 / 开头的请求会自动拼接 */
ttx.ajax.baseUrl = '';

ttx.ajax.interceptors = {
    request: [],
    response: []
};

// 链式注册：ttx.ajax.interceptors.request.use(fn) / response.use(fn)
// 与既有 useRequest/useResponse 共享同一底层数组，向后兼容。
// use(fn) 返回注销函数，调用后该拦截器失效。
[ttx.ajax.interceptors.request, ttx.ajax.interceptors.response].forEach(function (list) {
    list.use = function (handler) {
        if (typeof handler === 'function') list.push(handler);
        return function () { ttx.ajax.interceptors.eject(list, handler); };
    };
});
ttx.ajax.interceptors.eject = function (list, handler) {
    var idx = list.indexOf(handler);
    if (idx >= 0) list.splice(idx, 1);
};
ttx.ajax.interceptors.useRequest = function (handler) {
    if (typeof handler === 'function') ttx.ajax.interceptors.request.push(handler);
};
ttx.ajax.interceptors.useResponse = function (handler) {
    if (typeof handler === 'function') ttx.ajax.interceptors.response.push(handler);
};

/**
 * 错误规范化：统一失败结果为 Error 实例，携带 { code, message, url, status, httpStatus, raw, time }
 * 兼容既有 err instanceof Error / err.code / err.message / err.httpStatus 判断，
 * 并补充 url / status / raw（原始响应），供上层统一处理。
 * - isNetwork / isTimeout 标记用于可选重试判断。
 */
ttx.ajax.normalizeError = function (opts) {
    opts = opts || {};
    var err = new Error(opts.message || opts.msg || '请求失败');
    err.code = opts.code;
    err.httpStatus = opts.httpStatus;
    err.status = opts.status;
    err.url = opts.url;
    err.raw = opts.raw;
    if (opts.time) err.time = opts.time;
    if (opts.xhr) err.xhr = opts.xhr;
    if (opts.isNetwork) err.isNetwork = true;
    if (opts.isTimeout) err.isTimeout = true;
    if (opts.isHttpError) err.isHttpError = true;
    return err;
};

/**
 * 是否为登录态失效（401）。
 * 后端统一返回 HTTP 200 + body.code=401，这里同时兼容 httpStatus / 字符串码。
 */
ttx.ajax.isAuthExpired = function (res) {
    if (!res || typeof res !== 'object') return false;
    return Number(res.code) === 401 || Number(res.httpStatus) === 401;
};

/**
 * 原生XHR请求工具函数
 * @param {Object} options - 请求配置
 * @param {string} options.url - 请求地址
 * @param {string} [options.method='GET'] - 请求方法
 * @param {Object} [options.data] - 请求数据
 * @param {boolean} [options.async=true] - 是否异步请求
 * @param {Object} [options.headers] - 请求头配置
 * @param {number} [options.timeout=30000] - 超时时间(毫秒)
 * @param {Function} [options.onUploadProgress] - 上传进度回调 ({ percent, loaded, total })
 * @param {string} [options.responseType='json'] - 响应类型: json/text/blob/arraybuffer/document
 * @param {boolean} [options.validateCode=true] - 是否校验 response.code===200
 * @param {boolean} [options.showErrorToast=false] - 是否自动提示错误
 * @param {number} [options.retry=0] - 传输层失败(网络/超时/非2xx)最大重试次数
 * @param {number|Function} [options.retryDelay=1000] - 重试间隔(ms)，或 (attempt)=>ms
 * @param {Function} [options.success] - 成功回调函数
 * @param {Function} [options.error] - 错误回调函数
 * @param {Function} [options.complete] - 完成回调函数(无论成功失败都会执行)
 */
ttx.ajax.xhrUtil = function (options) {
    const defaults = {
        method: 'GET',
        async: true,
        timeout: 30000,
        responseType: 'json',
        validateCode: true,
        showErrorToast: true,
        headers: {
            'Content-Type': 'application/json;charset=utf-8'
        }
    };

    let config = { ...defaults, ...(options || {}) };
    config.headers = { ...defaults.headers, ...(options?.headers || {}) };
    config.method = (config.method || 'GET').toUpperCase();

    // 可选重试：retry=最大重试次数(传输层失败才重试)；retryDelay=间隔ms或函数(attempt)=>ms
    config.retry = Math.max(0, Number(config.retry) || 0);
    if (config.async === false) config.retry = 0; // 同步请求不重试
    config.retryDelay = (typeof config.retryDelay === 'function')
        ? config.retryDelay
        : (Number(config.retryDelay) >= 0 ? Number(config.retryDelay) : 1000);

    ttx.ajax.interceptors.request.forEach(handler => {
        const next = handler(config);
        if (next && typeof next === 'object') config = next;
    });

    let url = config.url || '';
    if (url.charAt(0) === '/' && !/^https?:\/\//i.test(url)) {
        var cfg = typeof ttx.ajax.config === 'function' ? ttx.ajax.config() : {};
        var prefix = cfg.platform || ttx.ajax.baseUrl || '';
        url = prefix + url;
    }
    let requestData = null;

    if (config.method === 'GET' && config.data) {
        const params = serializeParams(config.data);
        if (params) url += (url.includes('?') ? '&' : '?') + params;
    } else if (config.data !== undefined && config.data !== null) {
        const contentType = String(config.headers['Content-Type'] || '').toLowerCase();
        if (contentType.includes('application/json')) {
            requestData = JSON.stringify(config.data);
        } else if (contentType.includes('application/x-www-form-urlencoded')) {
            requestData = serializeParams(config.data);
        } else {
            requestData = config.data;
        }
    }

    const runComplete = function (xhr) {
        if (typeof config.complete === 'function') config.complete(xhr);
    };

    // 统一错误出口：err 已为规范化 Error（含 code/message/url/status/httpStatus/raw）。
    // 传输层失败(网络/超时/非2xx)且未耗尽重试时，延迟后重新发起请求。
    const runError = function (err, xhr, attempt) {
        if (!err.time) err.time = ttx.date.format(new Date());
        // access_token 过期：先用 refresh_token 静默续期并重放一次，避免直接报错
        if (ttx.ajax.isAuthExpired(err) && tryAuthRefresh(attempt, xhr)) return;
        // 401 统一友好文案，避免把后端的「Unauthorized」直接抛给用户
        if (ttx.ajax.isAuthExpired(err)) err.message = '登录状态已失效，请重新登录';
        // 仅传输层失败可重试；业务 code !== 200 不重试，避免重复提交
        const retryable = err.isNetwork || err.isTimeout || err.isHttpError;
        if (retryable && attempt < config.retry) {
            const delay = typeof config.retryDelay === 'function'
                ? config.retryDelay(attempt)
                : config.retryDelay;
            setTimeout(() => dispatch(attempt + 1), delay);
            return;
        }
        if (config.showErrorToast !== false && typeof ttx.toast !== 'undefined') {
            ttx.toast.error({
                message: err.message || '请求失败',
                code: err.code,
                httpStatus: err.httpStatus,
                time: err.time
            });
        }
        if (typeof config.error === 'function') config.error(err, xhr);
        // 兼容 fail 别名：tx.bill / tx.command / tx.codeRule 等大量调用点使用 fail 传错误回调，
        // 此前 fail 从未被调用，导致上层（如 voucher.init / tx.command.delete 的 load.hide()）在报错路径不执行。
        // runError 必须与 error 等价触发；为避免调用方同时传 error 和 fail（且指向同一函数）时重复回调，加一等价判断。
        // if (typeof config.fail === 'function' && config.fail !== config.error) config.fail(err, xhr);
        if (typeof config.fail === 'function') config.fail(err, xhr);
        runComplete(xhr);
    };

    // access_token 过期时：用 refresh_token 静默续期并重放一次请求
    let authRetried = false;
    const isAuthUrl = /\/api\/auth\/(login|refresh|logout)/.test(url);

    // 续期失败（refresh_token 缺失/过期/无效）：清本地登录态、弹出登录框并报错
    const finishAuthFailure = function (attempt, xhr) {
        if (ttx.auth && typeof ttx.auth.clear === 'function') ttx.auth.clear();
        if (ttx.auth && typeof ttx.auth.show === 'function') ttx.auth.show();
        runError(ttx.ajax.normalizeError({
            message: '登录状态已失效，请重新登录',
            code: 401,
            httpStatus: 401,
            status: 401,
            url: url,
            time: ttx.date.format(new Date())
        }), xhr, attempt);
    };

    const tryAuthRefresh = function (attempt, xhr) {
        if (authRetried || isAuthUrl) return false;
        if (!ttx.auth || typeof ttx.auth.refresh !== 'function') return false;
        if (!config.headers || !config.headers.Authorization) return false;
        authRetried = true;
        ttx.auth.refresh().then(function (ok) {
            if (!ok) {
                finishAuthFailure(attempt, xhr);
                return;
            }
            let token = null;
            try { token = localStorage.getItem('token'); } catch (e) { token = null; }
            if (token) config.headers.Authorization = 'Bearer ' + token;
            dispatch(attempt + 1);
        }).catch(function () {
            finishAuthFailure(attempt, xhr);
        });
        return true;
    };

    const dispatch = function (attempt) {
        const xhr = new XMLHttpRequest();

        xhr.open(config.method, url, config.async !== false);

        if (config.responseType && config.responseType !== 'json') {
            xhr.responseType = config.responseType;
        }

        Object.keys(config.headers || {}).forEach(key => {
            // 值为 null/undefined 表示显式移除默认头（如 FormData 上传时不能手写 Content-Type）
            if (config.headers[key] === null || config.headers[key] === undefined) return;
            xhr.setRequestHeader(key, config.headers[key]);
        });

        xhr.timeout = config.timeout;

        if (typeof config.onUploadProgress === 'function' && xhr.upload) {
            xhr.upload.onprogress = function (e) {
                if (e.lengthComputable) {
                    config.onUploadProgress({
                        percent: Math.round((e.loaded / e.total) * 100),
                        loaded: e.loaded,
                        total: e.total
                    });
                }
            };
        }

        xhr.onload = function () {
            if (!(xhr.status >= 200 && xhr.status < 300)) {
                runError(ttx.ajax.normalizeError({
                    message: `Request failed with status ${xhr.status}`,
                    code: null,
                    httpStatus: xhr.status,
                    status: xhr.status,
                    url: url,
                    isHttpError: true,
                    time: ttx.date.format(new Date())
                }), xhr, attempt);
                return;
            }

            let responseData;
            if (config.responseType === 'text') {
                responseData = xhr.responseText;
            } else if (config.responseType && config.responseType !== 'json') {
                responseData = xhr.response;
            } else {
                try {
                    responseData = JSON.parse(xhr.responseText);
                } catch (_e) {
                    responseData = xhr.responseText;
                }
            }

            // 业务码 401（后端统一 HTTP 200 + body.code）：先静默续期再重放，
            // 必须放在响应拦截器之前，否则上层拦截器会在续期前误弹登录框
            if (config.validateCode && responseData && typeof responseData === 'object'
                && 'code' in responseData && responseData.code !== 200
                && ttx.ajax.isAuthExpired(responseData) && tryAuthRefresh(attempt, xhr)) {
                return;
            }

            ttx.ajax.interceptors.response.forEach(handler => {
                const next = handler(responseData, xhr, config);
                if (next !== undefined) responseData = next;
            });

            if (config.validateCode && responseData && typeof responseData === 'object' && 'code' in responseData && responseData.code !== 200) {
                runError(ttx.ajax.normalizeError({
                    message: responseData.message || responseData.msg || '请求失败',
                    code: responseData.code,
                    httpStatus: responseData.httpStatus || xhr.status,
                    status: xhr.status,
                    url: url,
                    raw: responseData,
                    time: ttx.date.format(new Date())
                }), xhr, attempt);
                return;
            }

            if (typeof config.success === 'function') config.success(responseData, xhr);
            runComplete(xhr);
        };

        xhr.onerror = function () {
            runError(ttx.ajax.normalizeError({
                message: 'Network error occurred',
                httpStatus: 0,
                status: 0,
                url: url,
                isNetwork: true,
                time: ttx.date.format(new Date())
            }), xhr, attempt);
        };

        xhr.ontimeout = function () {
            runError(ttx.ajax.normalizeError({
                message: `Request timed out after ${config.timeout}ms`,
                httpStatus: 408,
                status: 0,
                url: url,
                isTimeout: true,
                time: ttx.date.format(new Date())
            }), xhr, attempt);
        };

        xhr.send(requestData);
        return xhr;
    };

    return dispatch(0);
};

ttx.ajax.request = function (options) {
    const config = { ...(options || {}), async: true };
    return new Promise((resolve, reject) => {
        ttx.ajax.xhrUtil({
            ...config,
            success: function (res, xhr) {
                resolve({ res, xhr });
            },
            error: function (err, xhr) {
                err.xhr = xhr;
                reject(err);
            }
        });
    });
};

ttx.ajax.requestSync = function (options) {
    let result;
    let error;
    ttx.ajax.xhrUtil({
        ...(options || {}),
        async: false,
        success: function (res, xhr) {
            result = { res, xhr };
        },
        error: function (err, xhr) {
            err.xhr = xhr;
            error = err;
        }
    });
    if (error) throw error;
    return result;
};

ttx.ajax.get = function (url, callback, options) {
    ttx.ajax.xhrUtil({
        ...(options || {}),
        url,
        method: 'GET',
        async: true,
        success: function (res) {
            if (typeof callback === 'function') callback(res?.data ?? res);
        }
    });
};

ttx.ajax.post = function (url, data, callback, options) {
    ttx.ajax.xhrUtil({
        ...(options || {}),
        url,
        method: 'POST',
        data,
        async: true,
        success: function (res) {
            if (typeof callback === 'function') callback(res?.data ?? res);
        }
    });
};

/**
 * 参数序列化工具函数
 * @param {Object} params - 需要序列化的参数对象
 * @returns {string} 序列化后的参数字符串
 */
function serializeParams(params) {
    if (!params || typeof params !== 'object') return '';
    return Object.keys(params)
        .map(key => {
            const value = params[key];
            if (value === null || value === undefined) {
                return encodeURIComponent(key) + '=';
            }
            if (typeof value === 'object') {
                return encodeURIComponent(key) + '=' + encodeURIComponent(JSON.stringify(value));
            }
            return encodeURIComponent(key) + '=' + encodeURIComponent(value);
        })
        .join('&');
}

/**
 * ========================= 使用示例 =========================
 * 1) 异步请求（Promise）
 * ttx.ajax.request({
 *   url: '/api/demo',
 *   method: 'POST',
 *   data: { id: 1 }
 * }).then(({ res, xhr }) => {
 *   // res: 业务响应数据
 *   // xhr: 原生 XMLHttpRequest 对象
 * }).catch((err) => {
 *   // err.xhr 可拿到失败时的 xhr
 * });
 *
 * 2) 同步请求（阻塞线程，仅在必须同步场景使用）
 * try {
 *   const { res, xhr } = ttx.ajax.requestSync({
 *     url: '/api/demo/sync',
 *     method: 'GET'
 *   });
 * } catch (err) {
 *   // 同步请求失败
 * }
 *
 * 3) GET/POST 快捷调用
 * ttx.ajax.get('/api/user', function (data) {
 *   // data 默认优先返回 res.data
 * });
 *
 * ttx.ajax.post('/api/user/save', { name: 'Tom' }, function (data) {
 *   // data 默认优先返回 res.data
 * });
 *
 * 4) 自定义响应类型（下载/文本）
 * ttx.ajax.request({
 *   url: '/api/file',
 *   method: 'GET',
 *   responseType: 'blob',
 *   validateCode: false
 * });
 *
 * 5) 拦截器
 * ttx.ajax.interceptors.request.use(function (config) {
 *   // 统一加 token
 *   config.headers = config.headers || {};
 *   config.headers['Authorization'] = 'Bearer xxx';
 *   return config; // 返回新的 config 替换；不返回则用原 config
 * });
 *
 * ttx.ajax.interceptors.response.use(function (res, xhr, config) {
 *   // 统一处理响应
 *   return res; // 返回 undefined 保留原 res，返回其他值替换 res
 * });
 *
 * // 注销拦截器：use 返回的注销函数调用后该拦截器失效
 * const ejectReq = ttx.ajax.interceptors.request.use(fn);
 * ejectReq();
 *
 * 6) 错误规范化：失败回调收到的 err 统一为 Error 实例，携带
 *    { code, message, url, status, httpStatus, raw, time }
 *    判断可用 err instanceof Error 或 err.code !== 200
 *
 * 7) 超时与可选重试
 * ttx.ajax.request({
 *   url: '/api/demo',
 *   timeout: 10000,      // 超时(ms)，默认 30000
 *   retry: 2,            // 传输层失败重试次数(网络/超时/非2xx)，默认 0
 *   retryDelay: 500      // 重试间隔(ms)，默认 1000；也可传函数 (attempt)=>ms
 * });
 *
 * 8) runError 触发场景
 * - HTTP 非 2xx
 * - 网络异常 xhr.onerror
 * - 超时 xhr.ontimeout
 * - validateCode=true 且 response.code !== 200
 * ============================================================
 */
